import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash';
import {
  Card, Table, CellText, Badge, Pagination, TextInput, Button, EmptyState, Spinner, SearchLg, AlertCircle,
} from '@opendate-app/setlist';
import client from '../../../shared/api/client';
import darkSelectStyles from '../../../shared/darkSelectStyles';
import {
  PAGE_SIZE, buildParams, buildExportUrl, serializeParams,
} from '../utils/allContactsParams';

export default function AllContactsTable({ table, contactLabel }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState({ key: 'last_seen', direction: 'desc' });
  const [filters, setFilters] = useState({ name: null, email: null, city: null, state: null, tags: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const drawRef = useRef(0);

  const loaders = useMemo(() => {
    const make = (url) => debounce((input, callback) => {
      client.get(url, { params: { name: input } })
        .then((res) => callback((res.data || []).map((option) => ({ value: option.name, label: option.name }))))
        .catch(() => {
          toast.error('Couldn\'t load filter options.');
          callback([]);
        });
    }, 250);
    return {
      name: make(table.filterOptionUrls.name),
      email: make(table.filterOptionUrls.email),
      city: make(table.filterOptionUrls.city),
      state: make(table.filterOptionUrls.state),
      tags: make(table.filterOptionUrls.tags),
    };
  }, [table.filterOptionUrls]);

  const setFilter = (key, value) => { setPage(1); setFilters((prev) => ({ ...prev, [key]: value })); };

  useEffect(() => {
    const handle = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    let ignore = false;
    drawRef.current += 1;
    setLoading(true);
    setError(false);

    client.get(table.dataUrl, {
      params: buildParams(drawRef.current, page, searchTerm, sort, filters),
      paramsSerializer: serializeParams,
    })
      .then((res) => {
        if (ignore) return;
        setRows(res.data.data || []);
        setTotal(res.data.recordsFiltered || 0);
      })
      .catch(() => {
        if (ignore) return;
        setRows([]);
        setTotal(0);
        setError(true);
        toast.error(`Couldn't load ${contactLabel.toLowerCase()}.`);
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });

    return () => { ignore = true; };
  }, [table.dataUrl, page, searchTerm, sort, filters, contactLabel]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const label = contactLabel.toLowerCase();

  const columns = [
    {
      key: 'name',
      header: 'Name',
      primary: true,
      sortable: true,
      render: (row) => (
        <a href={row.url} className="text-reset text-decoration-none">
          <CellText primary={row.name} supporting={row.email} />
        </a>
      ),
    },
    {
      key: 'total_events',
      header: 'Events',
      align: 'right',
      sortable: true,
      render: (row) => row.total_events,
    },
    {
      key: 'lifetime_value',
      header: 'Lifetime value',
      align: 'right',
      sortable: true,
      render: (row) => `$${Math.round(row.lifetime_value).toLocaleString()}`,
    },
    {
      key: 'market',
      header: 'Market',
      sortable: true,
      render: (row) => row.market || '—',
    },
    {
      key: 'last_seen',
      header: 'Last seen',
      sortable: true,
      render: (row) => row.last_seen || '—',
    },
    {
      key: 'tags',
      header: 'Tags',
      render: (row) => (row.tags || []).map((tag) => <Badge key={tag} color="gray">{tag}</Badge>),
    },
  ];

  return (
    <Card padding="md">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>All {label}</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <TextInput
            leadingIcon={SearchLg}
            placeholder={`Search ${label}`}
            aria-label={`Search ${label}`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <Button variant="secondary" onClick={() => { window.location.href = buildExportUrl(table.csvUrl, searchTerm, sort, filters); }}>Export CSV</Button>
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {[
          { key: 'name', placeholder: 'Name' },
          { key: 'email', placeholder: 'Email' },
          { key: 'city', placeholder: 'City' },
          { key: 'state', placeholder: 'State' },
        ].map((field) => (
          <div key={field.key} style={{ flex: '1 1 160px', minWidth: 140 }}>
            <AsyncSelect
              styles={darkSelectStyles}
              menuPortalTarget={document.querySelector('body')}
              placeholder={field.placeholder}
              aria-label={`Filter by ${field.placeholder.toLowerCase()}`}
              isClearable
              cacheOptions
              defaultOptions
              value={filters[field.key]}
              loadOptions={(input, callback) => loaders[field.key](input, callback)}
              onChange={(option) => setFilter(field.key, option)}
            />
          </div>
        ))}
        <div style={{ flex: '1 1 160px', minWidth: 140 }}>
          <AsyncSelect
            isMulti
            styles={darkSelectStyles}
            menuPortalTarget={document.querySelector('body')}
            placeholder="Tags"
            aria-label="Filter by tags"
            cacheOptions
            defaultOptions
            value={filters.tags}
            loadOptions={(input, callback) => loaders.tags(input, callback)}
            onChange={(options) => setFilter('tags', options || [])}
          />
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Spinner label={`Loading ${label}…`} />
        </div>
      ) : error ? (
        <EmptyState
          size="sm"
          icon={AlertCircle}
          title={`Couldn't load ${label}.`}
          description="Refresh the page to try again."
        />
      ) : (
        <Table
          columns={columns}
          data={rows}
          getRowKey={(row) => row.id}
          sort={sort}
          onSortChange={(key, direction) => { setPage(1); setSort({ key, direction }); }}
          empty={(
            <EmptyState
              size="sm"
              icon={SearchLg}
              title={`No ${label} found.`}
              description={searchTerm ? 'Try a different search term.' : undefined}
            />
          )}
        />
      )}

      {!loading && rows.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </Card>
  );
}
